// src/components/ProtectedRoute/ProtectedRoute.jsx
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const ProtectedRoute = ({ children, requiredRole, requiredUserType }) => {
  const { currentUser, userType, loading } = useAuth();
  const location = useLocation();

  // Debug logging
  useEffect(() => {
    console.log('=== PROTECTED ROUTE DEBUG ===');
    console.log('Path:', location.pathname);
    console.log('Loading:', loading);
    console.log('Current User:', currentUser);
    console.log('User Type:', userType);
    console.log('Token exists:', !!localStorage.getItem('token'));
    console.log('Required Role:', requiredRole);
    console.log('Required User Type:', requiredUserType);
    
    // Check localStorage directly
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedUserType = localStorage.getItem('userType');
    
    console.log('LocalStorage - Token:', token ? 'Exists' : 'Missing');
    console.log('LocalStorage - User:', storedUser ? 'Exists' : 'Missing');
    console.log('LocalStorage - UserType:', storedUserType);
    
    if (token && !currentUser && !loading) {
      console.warn('WARNING: Token exists but currentUser is null!');
    }
  }, [currentUser, userType, loading, location.pathname]);

  if (loading) {
    console.log('ProtectedRoute: Loading state...');
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Debug: Log what's happening
  console.log('ProtectedRoute: After loading check');
  console.log('Has currentUser:', !!currentUser);

  if (!currentUser) {
    console.log('ProtectedRoute: No currentUser, redirecting to /login');
    
    // Additional check for localStorage
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token || storedUser) {
      console.error('ERROR: Auth state mismatch!');
      console.error('Token in localStorage:', !!token);
      console.error('User in localStorage:', !!storedUser);
      console.error('But currentUser is:', currentUser);
      
      // Force reload to sync state
      setTimeout(() => {
        console.log('Force reloading due to auth state mismatch...');
        window.location.reload();
      }, 100);
    }
    
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check user type if specified
  if (requiredUserType) {
    const userTypes = Array.isArray(requiredUserType) ? requiredUserType : [requiredUserType];
    console.log('Checking user type:', userType, 'against required:', userTypes);
    
    if (!userTypes.includes(userType)) {
      console.log(`ProtectedRoute: User type ${userType} not allowed, redirecting to /unauthorized`);
      return <Navigate to="/unauthorized" replace state={{ from: location }} />;
    }
  }

  // Check role if specified
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    console.log('Checking role:', currentUser.role, 'against required:', roles);
    
    // Special handling for different user types
    if (userType === 'company' && currentUser.role === 'admin') {
      // Company admin - check for special permissions
      if (!roles.includes('admin') && !roles.includes('company-admin') && !roles.includes('all')) {
        console.log('ProtectedRoute: Company admin not authorized for this route');
        return <Navigate to="/unauthorized" replace state={{ from: location }} />;
      }
    } else if (userType === 'system' && currentUser.role === 'admin') {
      // System admin - check for system admin permissions
      if (!roles.includes('admin') && !roles.includes('system-admin') && !roles.includes('all')) {
        console.log('ProtectedRoute: System admin not authorized for this route');
        return <Navigate to="/unauthorized" replace state={{ from: location }} />;
      }
    } else if (!roles.includes(currentUser.role)) {
      // Regular role check for other users
      console.log(`ProtectedRoute: Role ${currentUser.role} not allowed, redirecting to /unauthorized`);
      return <Navigate to="/unauthorized" replace state={{ from: location }} />;
    }
  }

  console.log('ProtectedRoute: Access granted, rendering children');
  console.log('User authenticated as:', currentUser.name || currentUser.email);
  console.log('User type:', userType);
  console.log('User role:', currentUser.role);

  return children;
};

export default ProtectedRoute;